/**
 * clients - the operator-facing client list and the selected-client store.
 *
 * The console is multi-client from 2026-09-03 (client #2 = Zing Health). The API takes `client` on
 * every call and partitions its data under Portal/08 Member Care/clients/<client>/, so the frontend
 * only has to (a) know which clients exist and (b) remember which one the operator is working. The
 * selection lives in localStorage (`membercare.client`) and defaults to `alignment`, so a fresh
 * browser behaves exactly as the single-tenant console did.
 *
 * WAVE 2 (2026-09-06): the list is READ FROM THE API (`GET /api/care/clients`, which is the same
 * list every route validates against: the registry when loaded, the compiled floor before) rather
 * than compiled here. The two-entry FLOOR below is what the console knows before that read lands
 * (and after a failed one), so the header is never empty; the loaded list replaces it. A slug is
 * "known" against the list in force at the time it is checked, which is why the ?client= hint has
 * THREE outcomes rather than two (see readHint / the load below): a slug the floor does not know is
 * PENDING until the list loads, then applied or refused; and when the list cannot be loaded the
 * console says it could not VERIFY the hint, which is a different statement from "not on the list".
 *
 * Three ways to read it:
 *  - `currentClient()` - synchronous, for non-React code (care-api builds every request off it).
 *  - `useClient()` - React hook; re-renders on change (useSyncExternalStore, no provider needed).
 *  - `useClientList()` / `useClientHint()` - React hooks over the list and the hint outcome.
 *
 * The list says nothing about data roots or credentials; those stay server-side.
 */
import { useSyncExternalStore } from 'react';
import { apiJson } from '@lib/apiClient';
import { validPortalLinkTemplate } from '@lib/portal-link';

export interface ClientDef {
  /** Slug the API accepts as `client` (also the branding folder + html[data-tenant]). */
  slug: string;
  /** Full name for the header chip. */
  displayName: string;
  /** Short name for tight chrome (nav meta). */
  shortName: string;
  /** Whether member lookup answers for this client on this deployment (absent = unknown). */
  rosterAvailable?: boolean;
  /** The campaign's delivery route (`bench`, `analyzer`, `device`), when the API named one. */
  route?: string | null;
  /** R. 81: the campaign a lookup reads when nothing names one, when the API said (absent on the floor). */
  referenceCampaign?: string | null;
  /** Row 419: per campaign, the registry's program family code and year, when the API named both (absent on the floor). */
  programScopes?: Record<string, { program: string; year: number }>;
  /** M1 part B: the plan's OWN reply mailbox when member email is on and the registry names one (absent otherwise). */
  replyMailbox?: string;
}

/** The compiled floor: what the console knows before the client list has been read. */
export const CLIENTS: readonly ClientDef[] = [
  { slug: 'alignment', displayName: 'Alignment Health Plan', shortName: 'Alignment' },
  { slug: 'zing', displayName: 'Zing Health', shortName: 'Zing' },
];

export const DEFAULT_CLIENT = 'alignment';
export const CLIENT_STORAGE_KEY = 'membercare.client';

/** How the list in force was obtained. */
export type ClientListSource = 'floor' | 'loaded' | 'unverifiable';

let list: readonly ClientDef[] = CLIENTS;
let listSource: ClientListSource = 'floor';
let listError = '';

/** The client list in force: the loaded one, or the floor. */
export function clientList(): readonly ClientDef[] {
  return list;
}

export function clientListSource(): { source: ClientListSource; error: string } {
  return { source: listSource, error: listError };
}

export function isKnownClient(slug: string | null | undefined): slug is string {
  return typeof slug === 'string' && list.some((c) => c.slug === slug);
}

export function clientDef(slug: string): ClientDef {
  return list.find((c) => c.slug === slug) ?? list[0] ?? CLIENTS[0]!;
}

export function clientDisplayName(slug: string): string {
  return clientDef(slug).displayName;
}

/* ----------------------------- store ----------------------------- */

/** The raw stored slug (may be unknown to the floor yet known to the loaded list). */
function storedRaw(): string {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(CLIENT_STORAGE_KEY) : null;
    return raw?.trim().toLowerCase() ?? '';
  } catch {
    return '';
  }
}

function readStored(): string {
  const slug = storedRaw();
  return isKnownClient(slug) ? slug : DEFAULT_CLIENT;
}

/* ------------------------- the Workflow Hub's ?client= hint ------------------------- */

/**
 * What happened to a `?client=` parameter on the landing URL. The Hub's URL contract (2026-09-04,
 * section 1.3) requires the console to say when it could NOT honour the hint, so this is rendered as
 * a notice rather than swallowed. THREE outcomes for a named slug, not two (the rule raised with
 * every station on 2026-09-04, when the plan moved the list onto the network):
 *   applied       the slug is on the list in force
 *   unknown       the LOADED list was checked and does not name it
 *   unverifiable  the list could not be loaded, so nobody can say; the remembered choice stands
 * plus `pending` while the floor does not know the slug and the load has not answered yet.
 */
export type ClientHintOutcome =
  | { kind: 'none' }
  | { kind: 'applied'; slug: string }
  | { kind: 'pending'; value: string }
  | { kind: 'repeated' }
  | { kind: 'unknown'; value: string }
  | { kind: 'unverifiable'; value: string; error: string };

let hintOutcome: ClientHintOutcome = { kind: 'none' };

/** The outcome of the landing URL's `client` hint, for the notice in the header. */
export function clientHint(): ClientHintOutcome {
  return hintOutcome;
}

/**
 * Read `?client=<slug>` from the landing URL.
 *
 * It is a PRESELECTION HINT and nothing else: it sets this control, it is never an authorization and
 * never a data root (Hub contract 1.2). Every API call still carries the client as its own parameter
 * and the API validates it there, so a hand-typed slug here can widen nothing.
 *
 *  - absent or empty  -> no opinion; the operator's remembered choice stands (contract 1.1)
 *  - repeated         -> invalid; refused rather than resolved, because first-or-last is a guess
 *  - unknown to the floor -> PENDING; the remembered choice stands until the list loads
 *  - known slug       -> applied, and adopted into the station's own state (contract 1.4 allows this)
 */
function readHint(): { slug: string | null; outcome: ClientHintOutcome } {
  try {
    if (typeof window === 'undefined' || !window.location?.search) return { slug: null, outcome: { kind: 'none' } };
    const all = new URLSearchParams(window.location.search).getAll('client');
    if (all.length === 0) return { slug: null, outcome: { kind: 'none' } };
    if (all.length > 1) return { slug: null, outcome: { kind: 'repeated' } };
    const raw = (all[0] ?? '').trim().toLowerCase();
    if (!raw) return { slug: null, outcome: { kind: 'none' } };
    if (!isKnownClient(raw)) return { slug: null, outcome: { kind: 'pending', value: raw } };
    return { slug: raw, outcome: { kind: 'applied', slug: raw } };
  } catch {
    return { slug: null, outcome: { kind: 'none' } };
  }
}

function persist(slug: string): void {
  try {
    localStorage.setItem(CLIENT_STORAGE_KEY, slug);
  } catch {
    /* quota / unavailable: the in-memory selection still governs this session */
  }
}

function initialSelection(): string {
  const { slug, outcome } = readHint();
  hintOutcome = outcome;
  if (!slug) return readStored();
  // Adopt the hint as the station's own state so it survives in-app navigation.
  persist(slug);
  return slug;
}

let selected: string = initialSelection();
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of Array.from(listeners)) fn();
}

/** The client every API call is made for, right now. */
export function currentClient(): string {
  return selected;
}

/**
 * Change the working client. Unknown slugs are ignored (the list is the whitelist, so a stale or
 * hand-edited localStorage value can never put an unlisted slug on the wire).
 */
export function setCurrentClient(slug: string): void {
  const next = slug.trim().toLowerCase();
  if (!isKnownClient(next) || next === selected) return;
  selected = next;
  persist(next);
  notify();
}

/** Subscribe to selection, list, or hint changes; returns the unsubscribe. */
export function subscribeClient(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/* ------------------------------ the list, read from the API ------------------------------ */

/**
 * Row 419: the program scopes as the API sent them, kept only when well-formed (a slug to a family code
 * and an integer year); anything else reads as absent, so the tab says "this campaign only".
 */
function programScopesOf(v: unknown): { programScopes?: Record<string, { program: string; year: number }> } {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: Record<string, { program: string; year: number }> = {};
  for (const [slug, sc] of Object.entries(v as Record<string, unknown>)) {
    const o = (sc && typeof sc === 'object' ? sc : {}) as { program?: unknown; year?: unknown };
    if (typeof o.program === 'string' && o.program.trim() && Number.isInteger(o.year)) out[slug] = { program: o.program.trim(), year: o.year as number };
  }
  return Object.keys(out).length ? { programScopes: out } : {};
}

/** M1 part B: a mailbox only when the API sent an address; null, '' or anything else is "none". */
function replyMailboxOf(v: unknown): { replyMailbox?: string } {
  return typeof v === 'string' && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim()) ? { replyMailbox: v.trim() } : {};
}

interface ClientsResp {
  clients: ClientDef[];
  source: 'registry' | 'floor';
  registry?: { configured: boolean; loaded: boolean; lastError: string | null };
  features?: Partial<Features>;
}

/**
 * What this deployment OFFERS (row 286). Read with the client list at sign-in; every flag is false
 * until the read lands and stays false when it fails, so a dark screen is never shown on a guess.
 */
export interface Features {
  /** The DO NOT CONTACT master table (nav 12), behind MEMBERCARE_DNC_MASTER. */
  dncMaster: boolean;
  /** Whether an admin decision reaches the texting platform today (MEMBERCARE_OPTOUT_PLATFORM_WRITE). */
  platformWrite: boolean;
  /** Row 372: tab 13's "Add a member to technical exclusion" panel, behind MEMBERCARE_TECH_EXCLUSION. */
  techExclusion: boolean;
  /** Row 391: the Inbound queue's reply groups and Opt In / Opt Out / do-not-contact buttons (MEMBERCARE_REPLY_BUTTONS). */
  replyButtons: boolean;
  /**
   * Row 400: the member's page on the main portal as a link shape (MEMBERCARE_PORTAL_MEMBER_LINK), with
   * {campaign} and {member} placeholders; '' when the deployment sets none, and then no name links.
   * Validated on the server and again here (lib/portal-link.ts) before any URL is built from it.
   */
  portalMemberLink: string;
  /**
   * Row 410: the Video (test) tab (MEMBERCARE_VIDEO_LINKS). `off` = nothing shown; `test` = the tab for
   * the operators on the test list; `on` is reserved for the later member-drawer button and shows the
   * tab exactly as `test` does for now. Anything the API did not say in those three words is `off`.
   */
  videoLinks: VideoLinksMode;
  /** Row 410: whether THE SIGNED-IN OPERATOR is on the test list, as the API computed it for the caller. */
  videoTestAllowed: boolean;
  /** Row 578a (R. 128): the Restart after STOP button (MEMBERCARE_STOP_RESTART). */
  stopRestart: boolean;
  /** PL-009 K4: presses go to the master's door (MEMBERCARE_MASTER_DECISIONS); the shell runs the read-back. */
  masterDecisions: boolean;
  /** M1 part B: member email from the entry's Email section (MEMBERCARE_EMAIL_REPLY); the plan's mailbox rides its ClientDef. */
  emailReply: boolean;
  /**
   * M1-C (row 872-5): the drawer's Opt Out offers Email and This campaign only (MEMBERCARE_OPTOUT_EMAIL). Off,
   * the drawer shows exactly today's options; the server refuses both scopes by name either way.
   */
  optOutEmail: boolean;
  /**
   * Wave 3 item 4 (William, 2 Oct 2026: "all items go to approval then take effect"): a press bound to
   * one case is QUEUED for approval and takes effect when the case is approved (MEMBERCARE_STAGED_PRESSES).
   * Off, every press acts at once exactly as before, and the screens are what they were.
   */
  stagedPresses: boolean;
  /**
   * Opt-out everywhere (William, 21 Oct 2026; MEMBERCARE_OPTOUT_EVERYWHERE): the queue's Opt-Out and Stop send each
   * queued row to the Approval Queue at once (the row leaves the list), and a matched call or email asking out is
   * pressed from the queue as a Match Member with the stop decision. Off, the queue bar is exactly today's.
   */
  optOutEverywhere: boolean;
  /**
   * One-click opt-in (William, 22 Oct 2026; MEMBERCARE_OPTIN_EVERYWHERE): the queue's Opt-In runs at the click and sends
   * each queued row to the Approval Queue (the row leaves the list). Off, Opt-In is exactly today's.
   */
  optInEverywhere: boolean;
  /**
   * Opt-out master write (William, 22 Oct 2026, BN1 a; MEMBERCARE_OPTOUT_MASTER_WRITE): approval also sends the decision
   * to the master database. True only when the server says 'on'. The drawer then offers Approve (retry) after a
   * retryable failed master send (lib/master-retry.ts). Off, the drawer is exactly today's.
   */
  optOutMasterWrite: boolean;
  /** BB1 (22 Oct 2026): the Unstop on Care's own line (MEMBERCARE_UNSTOP_CARE_LINE on, master link off): drawer and Lookup. */
  unstopCareLine: boolean;
}
export type VideoLinksMode = 'off' | 'test' | 'on';
function videoLinksModeOf(v: unknown): VideoLinksMode {
  return v === 'test' || v === 'on' ? v : 'off';
}
const NO_FEATURES: Features = { dncMaster: false, platformWrite: false, techExclusion: false, replyButtons: false, portalMemberLink: '', videoLinks: 'off', videoTestAllowed: false, stopRestart: false, masterDecisions: false, emailReply: false, optOutEmail: false, stagedPresses: false, optOutEverywhere: false, optInEverywhere: false, optOutMasterWrite: false, unstopCareLine: false };
let features: Features = NO_FEATURES;
export function currentFeatures(): Features { return features; }

let loadInFlight: Promise<void> | null = null;

/**
 * 5 Oct 2026 ("Care needs to show all of the plans"): a server whose registry has not loaded yet
 * answers its two-plan floor with source 'floor'. The server now waits for its registry before it
 * answers, so this is the second line: when the answer still says the registry is configured but
 * not loaded, the list is asked again after each of these pauses, and the loaded registry list
 * replaces the floor the moment one arrives. Bounded, so a dead registry costs three more reads.
 */
export const FLOOR_RETRY_DELAYS_MS: readonly number[] = [3_000, 6_000, 12_000];
let floorRetries = 0;
let floorRetryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Read the client list from the API and put it in force. Called once the operator is signed in
 * (the shell's mount); safe to call again. Resolves the pending hint and the stored selection
 * against the loaded list, and marks the hint UNVERIFIABLE when the read fails, so the screen can
 * say which of the two it is. Never throws.
 */
export function loadClientList(fetcher: () => Promise<ClientsResp> = () => apiJson<ClientsResp>('/api/care/clients')): Promise<void> {
  if (loadInFlight) return loadInFlight;
  loadInFlight = (async () => {
    try {
      const resp = await fetcher();
      const loaded = (resp?.clients ?? [])
        .filter((c) => c && typeof c.slug === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(c.slug))
        .map((c): ClientDef => ({ slug: c.slug, displayName: c.displayName || c.slug, shortName: c.shortName || c.displayName || c.slug, ...(typeof c.rosterAvailable === 'boolean' ? { rosterAvailable: c.rosterAvailable } : {}), route: c.route ?? null, ...(typeof c.referenceCampaign === 'string' ? { referenceCampaign: c.referenceCampaign } : {}), ...(programScopesOf(c.programScopes)), ...(replyMailboxOf(c.replyMailbox)) }));
      if (loaded.length === 0) throw new Error('the API named no clients');
      list = loaded;
      listSource = 'loaded';
      listError = '';
      // A flag is on only when the API said `true`; anything else (absent, null, a string) is off.
      features = {
        dncMaster: resp?.features?.dncMaster === true,
        platformWrite: resp?.features?.platformWrite === true,
        techExclusion: resp?.features?.techExclusion === true,
        replyButtons: resp?.features?.replyButtons === true,
        portalMemberLink: validPortalLinkTemplate(resp?.features?.portalMemberLink),
        videoLinks: videoLinksModeOf(resp?.features?.videoLinks),
        videoTestAllowed: resp?.features?.videoTestAllowed === true,
        stopRestart: resp?.features?.stopRestart === true,
        masterDecisions: resp?.features?.masterDecisions === true,
        emailReply: resp?.features?.emailReply === true,
        optOutEmail: resp?.features?.optOutEmail === true,
        stagedPresses: resp?.features?.stagedPresses === true,
        optOutEverywhere: resp?.features?.optOutEverywhere === true,
        optInEverywhere: resp?.features?.optInEverywhere === true,
        // The server sends the state by name ('on', 'off', 'off-k4-on', 'off-everywhere-off'); only 'on' is on.
        optOutMasterWrite: (resp?.features as Record<string, unknown> | undefined)?.['optOutMasterWrite'] === 'on',
        unstopCareLine: resp?.features?.unstopCareLine === true,
      };
      // The hint, now checked against the loaded list: applied, or unknown for certain.
      if (hintOutcome.kind === 'pending') {
        const value = hintOutcome.value;
        if (isKnownClient(value)) {
          hintOutcome = { kind: 'applied', slug: value };
          selected = value;
          persist(value);
        } else {
          hintOutcome = { kind: 'unknown', value };
        }
      } else if (hintOutcome.kind === 'applied' && !isKnownClient(hintOutcome.slug)) {
        // The floor knew it and the registry does not: the registry wins, and the screen says so.
        hintOutcome = { kind: 'unknown', value: hintOutcome.slug };
      }
      // The operator's remembered choice, if the floor could not honour it but the list can.
      const remembered = storedRaw();
      if (hintOutcome.kind !== 'applied' && remembered && remembered !== selected && isKnownClient(remembered)) {
        selected = remembered;
      }
      // A selection the loaded list does not name falls back rather than riding to the API.
      if (!isKnownClient(selected)) {
        selected = isKnownClient(DEFAULT_CLIENT) ? DEFAULT_CLIENT : list[0]!.slug;
        persist(selected);
      }
      // The server's floor while its registry is still loading: ask again shortly (bounded).
      if (resp?.source === 'floor' && resp.registry?.configured === true && resp.registry.loaded !== true) {
        const delay = FLOOR_RETRY_DELAYS_MS[floorRetries];
        if (delay !== undefined && !floorRetryTimer) {
          floorRetries += 1;
          floorRetryTimer = setTimeout(() => {
            floorRetryTimer = null;
            void loadClientList(fetcher);
          }, delay);
        }
      } else {
        floorRetries = 0;
      }
    } catch (err) {
      listSource = 'unverifiable';
      listError = err instanceof Error ? err.message : String(err);
      if (hintOutcome.kind === 'pending') hintOutcome = { kind: 'unverifiable', value: hintOutcome.value, error: listError };
    } finally {
      loadInFlight = null;
      notify();
    }
  })();
  return loadInFlight;
}

/** Test hook: drop the selection back to the default without touching storage. */
export function resetClientForTests(): void {
  selected = readStored();
}

/** Test hook: forget the loaded list and the hint outcome. */
export function resetClientListForTests(): void {
  list = CLIENTS;
  listSource = 'floor';
  listError = '';
  features = NO_FEATURES;
  loadInFlight = null;
  hintOutcome = { kind: 'none' };
  floorRetries = 0;
  if (floorRetryTimer) clearTimeout(floorRetryTimer);
  floorRetryTimer = null;
}

/** React hook: the selected client slug, re-rendering the caller when it changes. */
export function useClient(): string {
  return useSyncExternalStore(subscribeClient, currentClient, currentClient);
}

/** React hook: the client list in force, re-rendering when the API read lands. */
export function useClientList(): readonly ClientDef[] {
  return useSyncExternalStore(subscribeClient, clientList, clientList);
}

/** React hook: the hint outcome, re-rendering when the list read settles it. */
export function useClientHint(): ClientHintOutcome {
  return useSyncExternalStore(subscribeClient, clientHint, clientHint);
}

/** React hook: what the deployment offers, re-rendering when the sign-in read lands. */
export function useFeatures(): Features {
  return useSyncExternalStore(subscribeClient, currentFeatures, currentFeatures);
}

/**
 * True once the sign-in read has ANSWERED, loaded or failed (row 359 B). A screen whose shape depends
 * on a flag waits for this instead of drawing the flag-off shape first and swapping it out when the
 * read lands, which remounted the page, threw away what the operator had typed or ticked, and jumped
 * the view to the top once per sign-in. A failed read settles it too: the flags then stay off, said.
 */
export function featuresSettled(): boolean {
  return listSource !== 'floor';
}

/** React hook form of featuresSettled. */
export function useFeaturesSettled(): boolean {
  return useSyncExternalStore(subscribeClient, featuresSettled, featuresSettled);
}
